/**
 * builders/tasks-builder.js — constructores PUROS de los payloads JSON de los ops de task
 * (POST tasks/fromForms, POST tasks, POST tasks/updateFilds, POST tasks/changeStatus,
 * POST tasks/answer/{taskId}). Re-implementacion LIMPIA de los builders v1
 * (src/offline/modules/tasks-builder.js + task-actions-builder.js — REFERENCIA, NO importados).
 *
 * PUREZA (PUSH-05): cero SQL, cero transport, cero import de v1. Solo moldean la forma de los
 * campos del contrato 02-sync-contract.md §1. La resolucion client_uuid->server_id la hace el
 * handler ANTES de llamar a estos builders (el `taskId` que reciben es el server_id ya resuelto).
 *
 * Nombres de campo EXACTOS del backend (verificados en 02-sync-contract.md §1):
 *   - changeStatus lee `category`/`category2` (NO tag/tag2) y EXIGE `endDate` en 'Reasignada_edit'.
 *   - evidence distingue por la CLAVE: picture_evidence | comment_evidence | file_evidence.
 */

/**
 * task.create (in-occurrence) -> POST tasks/fromForms. El backend pone state:'editing'.
 * Solo se copian los campos del contrato; NUNCA el client_uuid.
 * @param {object} task
 * @returns {object}
 */
export const buildCreatePayload = (task) => {
  const t = task || {}
  const payload = {
    ocurrence_id: t.ocurrence_id ?? t.occurrence_id,
    form_id: t.form_id,
    role_id: t.role_id,
    item_id: t.item_id,
    // normalizeTaskFields remapea assigned_to -> assigned_to_id (la columna real); el backend fromForms
    // lee `assigned_to`. Aceptamos ambas formas para no mandar la task sin responsable offline.
    assigned_to: t.assigned_to ?? t.assigned_to_id,
    location_id: t.location_id,
    title: t.title ?? '',
    // fromForms EXIGE `state` sin fallback (TasksController@fromForms). El path online
    // (createTaskEditing) lo fuerza a 'editing'; el record offline ya lo persiste. Sin esta línea
    // el POST revienta con 500 "Undefined index: state" y la task nunca sincroniza.
    state: t.state ?? 'editing',
    pictures: Array.isArray(t.pictures) ? t.pictures : []
  }
  if (t.tag2 !== undefined) payload.tag2 = t.tag2
  if (t.area_id !== undefined) payload.area_id = t.area_id
  if (t.hierarchy_site_id !== undefined) payload.hierarchy_site_id = t.hierarchy_site_id
  if (t.hierarchy_subcategory_id !== undefined) payload.hierarchy_subcategory_id = t.hierarchy_subcategory_id
  // TKT-1975: origen de la tarea. fromForms solo aplica el rol configurado del formulario si es
  // automática; sin la llave conserva el comportamiento anterior (ops encolados antes del cambio).
  if (t.auto_action_plan !== undefined) payload.auto_action_plan = t.auto_action_plan
  return payload
}

/**
 * task.createStandalone -> POST tasks (apiResource @store, create INGENUO sin dedup).
 * Acepta el record shaped por el repo (snake_case) o el taskQuery crudo (toma el 1er alias).
 * @param {object} task
 * @returns {object}
 */
export const buildCreateStandalonePayload = (task) => {
  const t = task || {}
  const pick = (...vals) => vals.find(v => v !== undefined)

  const payload = {
    role: pick(t.role, t.role_id),
    assigned_to: pick(t.assigned_to, t.assigned_to_id, t.users),
    location_id: pick(t.location_id),
    end_date: pick(t.end_date, t.endDate) ?? null,
    priority: pick(t.priority) ?? null,
    title: pick(t.title) ?? null,
    tag: pick(t.tag, t.category) ?? null,
    tag2: pick(t.tag2, t.category2) ?? null,
    area_id: pick(t.area_id) ?? null,
    enforce_evidence: pick(t.enforce_evidence, t.enforceEvidence) ?? null,
    short_name: pick(t.short_name) ?? null,
    hierarchy_site_id: pick(t.hierarchy_site_id) ?? null,
    hierarchy_subcategory_id: pick(t.hierarchy_subcategory_id) ?? null,
    pictures: Array.isArray(t.pictures) ? t.pictures : []
  }

  // opcionales del flujo occurrence (solo si vienen definidos)
  if (t.comment !== undefined && t.comment !== null) payload.comment = t.comment
  if (t.state !== undefined && t.state !== null) payload.state = t.state
  const ocurrenceId = pick(t.ocurrence_id, t.occurrence_id)
  if (ocurrenceId !== undefined && ocurrenceId !== null) payload.ocurrence_id = ocurrenceId
  const formId = pick(t.form_id)
  if (formId !== undefined && formId !== null) payload.form_id = formId
  const itemId = pick(t.item_id)
  if (itemId !== undefined && itemId !== null) payload.item_id = itemId

  return payload
}

/**
 * task.updateFields -> POST tasks/updateFilds. `taskId` = server_id YA resuelto. Solo se
 * incluyen los campos presentes (allowlist) para no pisar con undefined.
 * @param {string|number} taskId  server_id resuelto.
 * @param {object} changes
 * @returns {object}
 */
export const buildUpdateFieldsPayload = (taskId, changes) => {
  const c = changes || {}
  const payload = { taskId }
  // 'users' es OBLIGATORIO: el backend (TasksController@updateFilds) reasigna el responsable leyendo
  // SOLO $data['users'] (+ $data['role']); NO lee 'assigned_to'. Sin 'users' aquí, reasignar el
  // responsable por offline-v2 nunca llegaba al server (el builder v1 sí lo incluía). [PR review #4b]
  const FIELDS = [
    'priority', 'end_date', 'tag', 'tag2', 'enforce_evidence', 'area_id',
    'hierarchy_site_id', 'hierarchy_subcategory_id', 'category', 'category2',
    'role', 'users', 'assigned_to', 'title'
  ]
  for (const k of FIELDS) {
    if (c[k] !== undefined) payload[k] = c[k]
  }
  return payload
}

/**
 * task.changeStatus -> POST tasks/changeStatus. `taskId` = server_id YA resuelto. Dos contratos
 * sobre el mismo endpoint, distinguidos por `state`:
 *   - completar/suspender ('Finalizada'|'Suspendida'): el minimo (state/assigned_to/location_id/comment).
 *   - EDITAR ('Reasignada_edit'): el backend EXIGE `endDate` (422 si falta) y lee
 *     `category`/`category2` (NO tag/tag2). Cualquier campo de edicion definido se incluye.
 * @param {string|number} taskId  server_id resuelto.
 * @param {object} fields
 * @returns {object}
 */
export const buildChangeStatusPayload = (taskId, fields = {}) => {
  const payload = { taskId }
  const common = ['state', 'assigned_to', 'location_id', 'comment']
  const editFields = [
    'endDate', 'title', 'priority', 'role', 'category', 'category2', 'short_name',
    'enforce_evidence', 'area_id', 'hierarchy_site_id', 'hierarchy_subcategory_id'
  ]
  for (const key of [...common, ...editFields]) {
    if (fields[key] !== undefined) payload[key] = fields[key]
  }
  return payload
}

/**
 * task.evidence -> POST tasks/answer/{taskId}. El contrato distingue por la CLAVE presente:
 *   - picture -> { picture_evidence: <base64 dataURL> }  (base64 en JSON, F5; null tolerado)
 *   - comment -> { comment_evidence: <string> }          (F3 lo maneja completo)
 *   - file    -> { file_evidence: "<filename>-<base64>" } (F5; base64 null tolerado)
 * Builder PURO/tolerante: en F3 base64 es null para picture/file (binario diferido a F5).
 * @param {object} args  { kind, base64, comment, filename }
 * @returns {object}
 */
export const buildEvidencePayload = ({ kind, base64, comment, filename } = {}) => {
  if (kind === 'picture') return { picture_evidence: base64 ?? null }
  if (kind === 'comment') return { comment_evidence: comment }
  if (kind === 'file') return { file_evidence: base64 != null ? `${filename}-${base64}` : null }
  return {}
}

/**
 * task.evidence kind 'task_picture' -> POST tasks/updateFildImages. Foto de la TAREA (no evidencia
 * de cumplimiento): el backend la guarda como type 'task_picture', igual que el camino online
 * (TasksService.updateFildImages). Builder PURO/tolerante: base64 null -> sin `content`.
 * @param {object} args  { taskId, base64 }
 * @returns {object}
 */
export const buildTaskPicturePayload = ({ taskId, base64 } = {}) => {
  const payload = { taskId }
  if (base64 != null) payload.content = base64
  return payload
}
